import {
  createAppointmentBookingDraftStrictPersisted,
  inspectAppointmentBookingDraftPersisted,
  removeAppointmentBookingDraftStrictPersisted,
} from "@/app/booking/core/appointmentDraftStore";
import {
  validateAppointmentBookingDraft,
  validateAppointmentStepOneBookingDraft,
  type AppointmentBookingDraftV1,
} from "@/app/booking/core/appointmentBookingDraft";
import type { AppointmentStaffChoiceEvidenceV2 } from "@/app/booking/scheduling/appointmentStaffChoiceEvidence";
import type { AppointmentTimeProfessionalMutationEvidenceV1 } from "@/app/booking/scheduling/appointmentTimeProfessionalMutation";
import { createAppointmentStepOneDraftFingerprint } from "@/app/booking/core/appointmentDraftFingerprint";
import {
  createAppointmentIntakeDraftStrictPersisted,
  inspectAppointmentIntakeDraftPersisted,
  removeAppointmentIntakeDraftStrictPersisted,
} from "@/app/booking/core/appointmentIntakeDraftStore";
import {
  createAppointmentReviewDraftStrictPersisted,
  inspectAppointmentReviewDraftPersisted,
  removeAppointmentReviewDraftStrictPersisted,
} from "@/app/booking/core/appointmentReviewDraftStore";
import {
  createAppointmentSchedulingDraftStrictPersisted,
  inspectAppointmentSchedulingDraftPersisted,
  removeAppointmentSchedulingDraftStrictPersisted,
} from "@/app/booking/core/appointmentSchedulingDraftStore";
import { isSupportedSpecialistAppointmentWorkingDraftTransition } from "@/app/booking/adapters/specialistAppointmentCompletionContext";
import {
  createAppointmentPaymentUiSelectionDraftStrictPersisted,
  inspectAppointmentPaymentUiSelectionDraftPersisted,
  removeAppointmentPaymentUiSelectionDraftStrictPersisted,
} from "@/app/booking/checkout/appointmentPaymentUiSelectionDraftStore";
import {
  createAppointmentStaffChoiceEvidenceStrictPersisted,
  inspectAppointmentStaffChoiceEvidencePersisted,
  removeAppointmentStaffChoiceEvidenceStrictPersisted,
} from "@/app/booking/scheduling/appointmentStaffChoiceEvidenceStore";
import {
  createAppointmentTimeProfessionalWorkingDraftStrictPersisted,
  inspectAppointmentTimeProfessionalWorkingDraftPersisted,
  removeAppointmentTimeProfessionalWorkingDraftStrictPersisted,
} from "@/app/booking/scheduling/appointmentTimeProfessionalWorkingDraftStore";
import {
  createAppointmentWaitlistDraftStrictPersisted,
  inspectAppointmentWaitlistDraftsPersisted,
  removeAppointmentWaitlistDraftStrictPersisted,
} from "@/app/booking/waitlist/appointmentWaitlistDraftStore";
import {
  createAppointmentWaitlistPendingTransitionStrictPersisted,
  inspectAppointmentWaitlistPendingTransitionsPersisted,
  removeAppointmentWaitlistPendingTransitionStrictPersisted,
} from "@/app/booking/waitlist/appointmentWaitlistPendingTransitionStore";
import { inspectAppointmentWaitlistReceiptsPersisted } from "@/app/booking/waitlist/appointmentWaitlistReceiptStore";
import {
  createAppointmentWaitlistSubmissionAttemptStrictPersisted,
  inspectAppointmentWaitlistSubmissionAttemptPersisted,
  inspectAppointmentWaitlistSubmissionAttemptsPersisted,
  removeAppointmentWaitlistSubmissionAttemptStrictPersisted,
} from "@/app/booking/waitlist/appointmentWaitlistSubmissionAttemptStore";
import { validateAppointmentWaitlistSubmissionAttempt } from "@/app/booking/waitlist/appointmentWaitlistSubmissionAttempt";
import {
  validatePackagePurchaseDraft,
  type PackagePurchaseDraftV1,
} from "@/app/commerce/packages/packagePurchaseDraft";
import {
  createPackagePurchaseAttemptStrictPersisted,
  inspectPackagePurchaseAttemptPersisted,
  removePackagePurchaseAttemptStrictPersisted,
} from "@/app/commerce/packages/packagePurchaseAttemptStore";
import { validatePackagePurchaseAttempt } from "@/app/commerce/packages/packagePurchaseAttempt";
import {
  createPackagePurchaseDraftStrictPersisted,
  inspectPackagePurchaseDraftPersisted,
  removePackagePurchaseDraftStrictPersisted,
} from "@/app/commerce/packages/packagePurchaseDraftStore";
import { inspectPackagePurchaseReceiptPersisted } from "@/app/commerce/packages/packagePurchaseReceiptStore";
import {
  inspectSpecialistLocationGatewayAppointmentTerminalAttemptPresenceStrict,
  readSpecialistLocationGatewayAppointmentTerminalAttemptStrict,
} from "./appointmentAttemptOriginStore";

import { createSpecialistLocationGatewayFingerprint } from "./contract";
import { createSpecialistLocationGatewayAppointmentDestinationFingerprint } from "./appointmentDestinationFingerprint";
export {
  createSpecialistLocationGatewayAppointmentDestinationFingerprint,
  createSpecialistLocationGatewayAppointmentDestinationFingerprintFromStepOneFingerprint,
} from "./appointmentDestinationFingerprint";
import type {
  SpecialistLocationGatewayDestinationDraftProof,
  SpecialistLocationGatewayDestinationDraftTransaction,
  SpecialistLocationGatewayDestinationLifecycle,
  SpecialistLocationGatewayMutableDestinationProof,
  SpecialistLocationGatewayPostDraftDestinationInspector,
} from "./authorityStore";

export interface SpecialistLocationGatewayDestinationTransactionBundle {
  destinationDraftId: string;
  expectedDestinationFingerprint: string;
  transaction: SpecialistLocationGatewayDestinationDraftTransaction;
}

export function createSpecialistLocationGatewayPackageDestinationFingerprint(
  value: unknown,
) {
  const validation = validatePackagePurchaseDraft(value);
  return validation.valid
    ? createSpecialistLocationGatewayFingerprint([
        "package-purchase-v1",
        JSON.stringify(validation.draft),
      ])
    : null;
}

interface StrictDraftStore<Draft extends { draftId: string }> {
  create(value: Draft):
    | { status: "created"; draft: Draft }
    | { status: "conflict"; reason: string }
    | { status: "capacity" }
    | {
        status:
          "invalid" | "unreadable" | "persistence-failed" | "readback-mismatch";
      };
  inspect(
    draftId: string,
  ):
    | { status: "absent" }
    | { status: "present"; draft: Draft }
    | { status: "unreadable" };
  remove(value: Draft):
    | { status: "removed"; draft: Draft }
    | { status: "absent" }
    | { status: "conflict"; reason: string }
    | {
        status:
          "invalid" | "unreadable" | "persistence-failed" | "readback-mismatch";
      };
}

function sameDraft(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

function isAmbiguousStatus(status: string) {
  return (
    status === "unreadable" ||
    status === "persistence-failed" ||
    status === "readback-mismatch"
  );
}

function createTransactionBundle<Draft extends { draftId: string }>(input: {
  draft: Draft;
  fingerprint: string;
  store: StrictDraftStore<Draft>;
}): SpecialistLocationGatewayDestinationTransactionBundle {
  const proof = Object.freeze({
    destinationDraftId: input.draft.draftId,
    persistedFingerprint: input.fingerprint,
  });
  const proofMatches = (
    candidate: SpecialistLocationGatewayDestinationDraftProof,
  ) =>
    candidate.destinationDraftId === proof.destinationDraftId &&
    candidate.persistedFingerprint === proof.persistedFingerprint;

  return {
    destinationDraftId: input.draft.draftId,
    expectedDestinationFingerprint: input.fingerprint,
    transaction: {
      async createOrInspectExact() {
        const current = input.store.inspect(input.draft.draftId);
        if (current.status === "unreadable") return { status: "unreadable" };
        if (current.status === "present") {
          return sameDraft(current.draft, input.draft)
            ? { status: "present-exact", ...proof }
            : { status: "failed" };
        }
        const created = input.store.create(input.draft);
        if (
          created.status === "created" &&
          sameDraft(created.draft, input.draft)
        ) {
          return { status: "created", ...proof };
        }
        if (created.status === "conflict") {
          const raced = input.store.inspect(input.draft.draftId);
          return raced.status === "present" &&
            sameDraft(raced.draft, input.draft)
            ? { status: "present-exact", ...proof }
            : raced.status === "unreadable"
              ? { status: "unreadable" }
              : { status: "failed" };
        }
        return isAmbiguousStatus(created.status)
          ? { status: "unreadable" }
          : { status: "failed" };
      },
      async removeExact(candidate) {
        if (!proofMatches(candidate)) return { status: "failed" };
        const removed = input.store.remove(input.draft);
        if (removed.status === "removed") {
          return sameDraft(removed.draft, input.draft)
            ? { status: "removed", ...proof }
            : { status: "unreadable" };
        }
        if (removed.status === "absent") {
          return { status: "absent", ...proof };
        }
        return isAmbiguousStatus(removed.status)
          ? { status: "unreadable" }
          : { status: "failed" };
      },
    },
  };
}

export function createSpecialistLocationGatewayAppointmentDestinationTransaction(
  value: unknown,
) {
  const validation = validateAppointmentStepOneBookingDraft(value);
  if (!validation.valid) return null;
  const fingerprint =
    createSpecialistLocationGatewayAppointmentDestinationFingerprint(
      validation.draft,
    );
  if (!fingerprint) return null;
  return createTransactionBundle<AppointmentBookingDraftV1>({
    draft: validation.draft,
    fingerprint,
    store: {
      create: createAppointmentBookingDraftStrictPersisted,
      inspect: inspectAppointmentBookingDraftPersisted,
      remove: removeAppointmentBookingDraftStrictPersisted,
    },
  });
}

export function createSpecialistLocationGatewayPackageDestinationTransaction(
  value: unknown,
) {
  const validation = validatePackagePurchaseDraft(value);
  if (!validation.valid) return null;
  const fingerprint =
    createSpecialistLocationGatewayPackageDestinationFingerprint(
      validation.draft,
    );
  if (!fingerprint) return null;
  return createTransactionBundle<PackagePurchaseDraftV1>({
    draft: validation.draft,
    fingerprint,
    store: {
      create: createPackagePurchaseDraftStrictPersisted,
      inspect: inspectPackagePurchaseDraftPersisted,
      remove: removePackagePurchaseDraftStrictPersisted,
    },
  });
}

type ExactLifecycleDraft = Readonly<{
  draftId: string;
  [key: string]: unknown;
}>;

interface ExactLifecycleEntry {
  storeKey: string;
  draft: ExactLifecycleDraft;
  inspect(draftId: string): unknown;
  create(value: unknown): unknown;
  remove(value: unknown): unknown;
}

interface ExactLifecycleSnapshot {
  entries: readonly ExactLifecycleEntry[];
  mutableCleanupFingerprint: string;
}

type ExactLifecycleInspection =
  | { status: "ready"; snapshot: ExactLifecycleSnapshot }
  | { status: "absent" | "blocked" | "failed" | "unreadable" };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function cloneLifecycleDraft(value: unknown): ExactLifecycleDraft | null {
  try {
    const clone = JSON.parse(JSON.stringify(value)) as unknown;
    return isRecord(clone) &&
      typeof clone.draftId === "string" &&
      clone.draftId.length > 0
      ? (clone as ExactLifecycleDraft)
      : null;
  } catch {
    return null;
  }
}

function snapshotPersistedDraftInspection(
  value: unknown,
):
  | { status: "absent" }
  | { status: "unreadable" }
  | { status: "present"; draft: ExactLifecycleDraft }
  | null {
  try {
    if (!isRecord(value)) return null;
    if (
      Object.keys(value).length === 1 &&
      (value.status === "absent" || value.status === "unreadable")
    ) {
      return { status: value.status };
    }
    if (Object.keys(value).length !== 2 || value.status !== "present") {
      return null;
    }
    const draft = cloneLifecycleDraft(value.draft);
    return draft ? { status: "present", draft } : null;
  } catch {
    return null;
  }
}

function hashExactLifecycleSnapshot(value: string) {
  const seeds = [
    0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35, 0x27d4eb2f, 0x165667b1,
    0xd3a2646c, 0xfd7046c5,
  ];
  return seeds
    .map((seed) => {
      let result = seed >>> 0;
      for (let index = 0; index < value.length; index += 1) {
        result = Math.imul(result ^ value.charCodeAt(index), 16_777_619);
      }
      return (result >>> 0).toString(16).padStart(8, "0");
    })
    .join("");
}

function createMutableCleanupFingerprint(
  kind: "appointment" | "package",
  entries: readonly ExactLifecycleEntry[],
) {
  try {
    const serialized = JSON.stringify(
      entries.map(({ storeKey, draft }) => [storeKey, draft]),
    );
    return createSpecialistLocationGatewayFingerprint([
      `${kind}-mutable-cleanup-v1`,
      serialized.length,
      hashExactLifecycleSnapshot(serialized),
    ]);
  } catch {
    return null;
  }
}

function createLifecycleEntry(input: {
  storeKey: string;
  draft: unknown;
  inspect(draftId: string): unknown;
  create(value: unknown): unknown;
  remove(value: unknown): unknown;
}) {
  const draft = cloneLifecycleDraft(input.draft);
  return draft ? { ...input, draft } : null;
}

function snapshotStrictMutationResult(
  value: unknown,
  successStatus: "created" | "removed",
) {
  try {
    if (!isRecord(value) || typeof value.status !== "string") return null;
    if (value.status === successStatus) {
      const draft = cloneLifecycleDraft(value.draft);
      return draft ? { status: successStatus, draft } : null;
    }
    if (
      value.status === "absent" ||
      value.status === "capacity" ||
      value.status === "conflict" ||
      value.status === "invalid" ||
      value.status === "unreadable" ||
      value.status === "persistence-failed" ||
      value.status === "readback-mismatch"
    ) {
      return { status: value.status };
    }
    return null;
  } catch {
    return null;
  }
}

function inspectEntryExact(entry: ExactLifecycleEntry) {
  let inspected: unknown;
  try {
    inspected = entry.inspect(entry.draft.draftId);
  } catch {
    return "unreadable" as const;
  }
  const snapshot = snapshotPersistedDraftInspection(inspected);
  if (!snapshot || snapshot.status === "unreadable")
    return "unreadable" as const;
  if (snapshot.status === "absent") return "absent" as const;
  return sameDraft(snapshot.draft, entry.draft)
    ? ("present-exact" as const)
    : ("conflict" as const);
}

function restoreLifecycleEntries(entries: readonly ExactLifecycleEntry[]) {
  for (const entry of [...entries].reverse()) {
    const current = inspectEntryExact(entry);
    if (current === "present-exact") continue;
    if (current !== "absent") return false;
    let createdRaw: unknown;
    try {
      createdRaw = entry.create(entry.draft);
    } catch {
      return false;
    }
    const created = snapshotStrictMutationResult(createdRaw, "created");
    if (
      !created ||
      created.status !== "created" ||
      !sameDraft(created.draft, entry.draft)
    ) {
      return false;
    }
  }
  return entries.every((entry) => inspectEntryExact(entry) === "present-exact");
}

function removeLifecycleEntries(entries: readonly ExactLifecycleEntry[]) {
  const removed: ExactLifecycleEntry[] = [];
  for (const entry of entries) {
    let removedRaw: unknown;
    try {
      removedRaw = entry.remove(entry.draft);
    } catch {
      restoreLifecycleEntries(removed);
      return false;
    }
    const result = snapshotStrictMutationResult(removedRaw, "removed");
    if (
      !result ||
      result.status !== "removed" ||
      !sameDraft(result.draft, entry.draft)
    ) {
      restoreLifecycleEntries(removed);
      return false;
    }
    removed.push(entry);
  }
  return true;
}

function createDestinationLifecycle(input: {
  destinationDraftId: string;
  persistedFingerprint: string;
  inspectAggregate(): ExactLifecycleInspection;
}): SpecialistLocationGatewayDestinationLifecycle {
  const snapshots = new Map<string, ExactLifecycleSnapshot>();
  const proofMatches = (
    proof: SpecialistLocationGatewayMutableDestinationProof,
  ) =>
    proof.destinationDraftId === input.destinationDraftId &&
    proof.persistedFingerprint === input.persistedFingerprint;

  return {
    async inspectMutableCleanupExact() {
      let inspected: ExactLifecycleInspection;
      try {
        inspected = input.inspectAggregate();
      } catch {
        return { status: "unreadable" };
      }
      if (inspected.status === "blocked") {
        return { status: "terminal-or-ambiguous-blocked" };
      }
      if (inspected.status !== "ready") return { status: inspected.status };
      snapshots.set(
        inspected.snapshot.mutableCleanupFingerprint,
        inspected.snapshot,
      );
      return {
        status: "mutable-cleanup-exact",
        destinationDraftId: input.destinationDraftId,
        persistedFingerprint: input.persistedFingerprint,
        mutableCleanupFingerprint: inspected.snapshot.mutableCleanupFingerprint,
      };
    },
    async removeMutableExact(proof) {
      if (!proofMatches(proof)) return { status: "failed" };
      let inspected: ExactLifecycleInspection;
      try {
        inspected = input.inspectAggregate();
      } catch {
        return { status: "unreadable" };
      }
      if (inspected.status === "absent") return { status: "absent" };
      if (inspected.status !== "ready") {
        return {
          status:
            inspected.status === "blocked" ? "ambiguous" : inspected.status,
        };
      }
      if (
        inspected.snapshot.mutableCleanupFingerprint !==
        proof.mutableCleanupFingerprint
      ) {
        return { status: "failed" };
      }
      snapshots.set(proof.mutableCleanupFingerprint, inspected.snapshot);
      return removeLifecycleEntries(inspected.snapshot.entries)
        ? {
            status: "removed",
            destinationDraftId: proof.destinationDraftId,
            persistedFingerprint: proof.persistedFingerprint,
            mutableCleanupFingerprint: proof.mutableCleanupFingerprint,
          }
        : { status: "unreadable" };
    },
    async restoreMutableExact(proof) {
      if (!proofMatches(proof)) return { status: "failed" };
      const snapshot = snapshots.get(proof.mutableCleanupFingerprint);
      if (!snapshot) return { status: "unreadable" };
      let current: ExactLifecycleInspection;
      try {
        current = input.inspectAggregate();
      } catch {
        return { status: "unreadable" };
      }
      if (
        current.status === "ready" &&
        current.snapshot.mutableCleanupFingerprint ===
          proof.mutableCleanupFingerprint
      ) {
        return {
          status: "present-exact",
          destinationDraftId: proof.destinationDraftId,
          persistedFingerprint: proof.persistedFingerprint,
          mutableCleanupFingerprint: proof.mutableCleanupFingerprint,
        };
      }
      if (current.status !== "absent") return { status: "unreadable" };
      return restoreLifecycleEntries(snapshot.entries)
        ? {
            status: "restored",
            destinationDraftId: proof.destinationDraftId,
            persistedFingerprint: proof.persistedFingerprint,
            mutableCleanupFingerprint: proof.mutableCleanupFingerprint,
          }
        : { status: "unreadable" };
    },
  };
}

function snapshotPersistedRecordCollection(
  value: unknown,
  key: string,
):
  | { status: "absent" | "unreadable"; records: readonly [] }
  | { status: "present"; records: readonly Record<string, unknown>[] }
  | null {
  try {
    if (!isRecord(value) || Object.keys(value).length !== 2) return null;
    if (
      value.status !== "absent" &&
      value.status !== "present" &&
      value.status !== "unreadable"
    ) {
      return null;
    }
    const collection = value[key];
    if (!Array.isArray(collection)) return null;
    if (value.status !== "present") {
      return collection.length === 0
        ? { status: value.status, records: [] }
        : null;
    }
    const cloned = JSON.parse(JSON.stringify(collection)) as unknown;
    if (!Array.isArray(cloned) || !cloned.every(isRecord)) return null;
    return { status: "present", records: cloned };
  } catch {
    return null;
  }
}

function inspectOptionalEntry(input: {
  storeKey: string;
  draftId: string;
  inspect(draftId: string): unknown;
  create(value: unknown): unknown;
  remove(value: unknown): unknown;
}):
  | { status: "absent" | "unreadable" }
  | { status: "present"; entry: ExactLifecycleEntry } {
  let raw: unknown;
  try {
    raw = input.inspect(input.draftId);
  } catch {
    return { status: "unreadable" };
  }
  const inspected = snapshotPersistedDraftInspection(raw);
  if (!inspected || inspected.status === "unreadable") {
    return { status: "unreadable" };
  }
  if (inspected.status === "absent") return inspected;
  const entry = createLifecycleEntry({
    storeKey: input.storeKey,
    draft: inspected.draft,
    inspect: input.inspect,
    create: input.create,
    remove: input.remove,
  });
  return entry ? { status: "present", entry } : { status: "unreadable" };
}

function inspectCustomerOwnedTerminalDraft(
  inspect: (draftId: string) => unknown,
  draftId: string,
  expectedCustomerAccountId: string | null,
) {
  let raw: unknown;
  try {
    raw = inspect(draftId);
  } catch {
    return "unreadable" as const;
  }
  const inspected = snapshotPersistedDraftInspection(raw);
  if (!inspected || inspected.status === "unreadable") {
    return "unreadable" as const;
  }
  if (inspected.status === "absent") return "absent" as const;
  return isRecord(inspected.draft) &&
    inspected.draft.customerAccountId === expectedCustomerAccountId
    ? ("blocked" as const)
    : ("failed" as const);
}

function nestedDraftId(value: unknown, key: string) {
  return isRecord(value) &&
    isRecord(value[key]) &&
    typeof value[key].draftId === "string"
    ? value[key].draftId
    : null;
}

function createAppointmentLifecycleInspection(
  sourceDraft: AppointmentBookingDraftV1,
  expectedCustomerAccountId: string | null,
): ExactLifecycleInspection {
  const destinationDraftId = sourceDraft.draftId;
  const canonicalPresence =
    inspectSpecialistLocationGatewayAppointmentTerminalAttemptPresenceStrict(
      destinationDraftId,
    );
  if (canonicalPresence.status === "unreadable") {
    return { status: "unreadable" };
  }
  if (canonicalPresence.status === "present") {
    // Active, terminal, and Receipt aggregate states are all explicit
    // lifecycle boundaries. They must be retired/reconciled by their typed
    // owner before the destination cleanup transaction can start.
    if (expectedCustomerAccountId === null) return { status: "blocked" };
    const canonicalTerminalAttempt =
      readSpecialistLocationGatewayAppointmentTerminalAttemptStrict({
        draftId: destinationDraftId,
        expectedCustomerAccountId,
      });
    if (canonicalTerminalAttempt.status === "unreadable") {
      return { status: "unreadable" };
    }
    if (canonicalTerminalAttempt.status === "owner-mismatch") {
      return { status: "failed" };
    }
    return { status: "blocked" };
  }
  let waitlistDraftsRaw: unknown;
  let waitlistReceiptsRaw: unknown;
  let waitlistAttemptsRaw: unknown;
  let pendingTransitionsRaw: unknown;
  try {
    waitlistDraftsRaw = inspectAppointmentWaitlistDraftsPersisted();
    waitlistReceiptsRaw = inspectAppointmentWaitlistReceiptsPersisted();
    waitlistAttemptsRaw =
      inspectAppointmentWaitlistSubmissionAttemptsPersisted();
    pendingTransitionsRaw =
      inspectAppointmentWaitlistPendingTransitionsPersisted();
  } catch {
    return { status: "unreadable" };
  }
  const waitlistDrafts = snapshotPersistedRecordCollection(
    waitlistDraftsRaw,
    "drafts",
  );
  const waitlistReceipts = snapshotPersistedRecordCollection(
    waitlistReceiptsRaw,
    "drafts",
  );
  const waitlistAttempts = snapshotPersistedRecordCollection(
    waitlistAttemptsRaw,
    "attempts",
  );
  const pendingTransitions = snapshotPersistedRecordCollection(
    pendingTransitionsRaw,
    "transitions",
  );
  if (
    !waitlistDrafts ||
    !waitlistReceipts ||
    !waitlistAttempts ||
    !pendingTransitions ||
    waitlistDrafts.status === "unreadable" ||
    waitlistReceipts.status === "unreadable" ||
    waitlistAttempts.status === "unreadable" ||
    pendingTransitions.status === "unreadable"
  ) {
    return { status: "unreadable" };
  }
  const ownedWaitlistDrafts = waitlistDrafts.records.filter(
    (draft) => nestedDraftId(draft, "appointmentDraft") === destinationDraftId,
  );
  if (
    ownedWaitlistDrafts.some(
      (draft) =>
        !sameDraft(draft.appointmentDraft, sourceDraft) ||
        draft.customerAccountId !== expectedCustomerAccountId,
    )
  ) {
    return { status: "failed" };
  }
  const ownedPendingTransitions = pendingTransitions.records.filter(
    (transition) => transition.sourceAppointmentDraftId === destinationDraftId,
  );
  const ownedWaitlistAttempts = waitlistAttempts.records.filter(
    (attempt) =>
      nestedDraftId(attempt, "sourceAppointmentDraft") === destinationDraftId,
  );
  if (
    ownedPendingTransitions.some(
      (transition) =>
        transition.expectedCustomerAccountId !== expectedCustomerAccountId,
    ) ||
    ownedWaitlistAttempts.some(
      (attempt) =>
        !validateAppointmentWaitlistSubmissionAttempt(attempt).valid ||
        !sameDraft(attempt.sourceAppointmentDraft, sourceDraft) ||
        attempt.customerAccountId !== expectedCustomerAccountId ||
        !isRecord(attempt.waitlistDraft) ||
        attempt.waitlistDraft.customerAccountId !== expectedCustomerAccountId ||
        !isRecord(attempt.state),
    )
  ) {
    return { status: "failed" };
  }
  const matchingWaitlistReceipts = waitlistReceipts.records.filter(
    (receipt) => receipt.sourceAppointmentDraftId === destinationDraftId,
  );
  if (
    matchingWaitlistReceipts.some(
      (receipt) => receipt.customerAccountId !== expectedCustomerAccountId,
    )
  ) {
    return { status: "failed" };
  }
  const hasWaitlistTerminal =
    matchingWaitlistReceipts.length > 0 ||
    ownedWaitlistAttempts.some(
      (attempt) =>
        isRecord(attempt.state) && attempt.state.kind !== "not-applied",
    );
  if (hasWaitlistTerminal) return { status: "blocked" };

  const waitlistAttemptEntries: ExactLifecycleEntry[] = [];
  for (const attempt of [...ownedWaitlistAttempts].sort((left, right) =>
    String(left.draftId).localeCompare(String(right.draftId)),
  )) {
    const entry = createLifecycleEntry({
      storeKey: `appointment-waitlist-attempt-v1:${String(attempt.draftId)}`,
      draft: attempt,
      inspect: inspectAppointmentWaitlistSubmissionAttemptPersisted,
      create: createAppointmentWaitlistSubmissionAttemptStrictPersisted,
      remove: removeAppointmentWaitlistSubmissionAttemptStrictPersisted,
    });
    if (!entry) return { status: "unreadable" };
    waitlistAttemptEntries.push(entry);
  }

  const source = inspectOptionalEntry({
    storeKey: "appointment-source-v1",
    draftId: destinationDraftId,
    inspect: inspectAppointmentBookingDraftPersisted,
    create: createAppointmentBookingDraftStrictPersisted,
    remove: removeAppointmentBookingDraftStrictPersisted,
  });
  if (source.status !== "present") return { status: source.status };
  if (!sameDraft(source.entry.draft, sourceDraft)) return { status: "failed" };

  const optional = [
    inspectOptionalEntry({
      storeKey: "appointment-payment-ui-v1",
      draftId: destinationDraftId,
      inspect: inspectAppointmentPaymentUiSelectionDraftPersisted,
      create: createAppointmentPaymentUiSelectionDraftStrictPersisted,
      remove: removeAppointmentPaymentUiSelectionDraftStrictPersisted,
    }),
    inspectOptionalEntry({
      storeKey: "appointment-review-v1",
      draftId: destinationDraftId,
      inspect: inspectAppointmentReviewDraftPersisted,
      create: createAppointmentReviewDraftStrictPersisted,
      remove: removeAppointmentReviewDraftStrictPersisted,
    }),
    inspectOptionalEntry({
      storeKey: "appointment-intake-v1",
      draftId: destinationDraftId,
      inspect: inspectAppointmentIntakeDraftPersisted,
      create: createAppointmentIntakeDraftStrictPersisted,
      remove: removeAppointmentIntakeDraftStrictPersisted,
    }),
    inspectOptionalEntry({
      storeKey: "appointment-scheduling-v2",
      draftId: destinationDraftId,
      inspect: inspectAppointmentSchedulingDraftPersisted,
      create: createAppointmentSchedulingDraftStrictPersisted,
      remove: removeAppointmentSchedulingDraftStrictPersisted,
    }),
    inspectOptionalEntry({
      storeKey: "appointment-time-professional-working-v1",
      draftId: destinationDraftId,
      inspect: inspectAppointmentTimeProfessionalWorkingDraftPersisted,
      create: createAppointmentTimeProfessionalWorkingDraftStrictPersisted,
      remove: removeAppointmentTimeProfessionalWorkingDraftStrictPersisted,
    }),
    inspectOptionalEntry({
      storeKey: "appointment-staff-choice-v2",
      draftId: destinationDraftId,
      inspect: inspectAppointmentStaffChoiceEvidencePersisted,
      create: createAppointmentStaffChoiceEvidenceStrictPersisted,
      remove: removeAppointmentStaffChoiceEvidenceStrictPersisted,
    }),
  ];
  if (optional.some(({ status }) => status === "unreadable")) {
    return { status: "unreadable" };
  }
  const optionalEntries = optional.flatMap((candidate) =>
    candidate.status === "present" ? [candidate.entry] : [],
  );
  const byKey = new Map(
    optionalEntries.map((entry) => [entry.storeKey, entry]),
  );
  const scheduling = byKey.get("appointment-scheduling-v2")?.draft;
  const intake = byKey.get("appointment-intake-v1")?.draft;
  const review = byKey.get("appointment-review-v1")?.draft;
  const paymentUi = byKey.get("appointment-payment-ui-v1")?.draft;
  const working = byKey.get("appointment-time-professional-working-v1")?.draft;
  const staff = byKey.get("appointment-staff-choice-v2")?.draft;

  if (
    (intake && intake.customerAccountId !== expectedCustomerAccountId) ||
    (review && review.customerAccountId !== expectedCustomerAccountId) ||
    (paymentUi && paymentUi.customerAccountId !== expectedCustomerAccountId)
  ) {
    return { status: "failed" };
  }

  if (
    (staff && !scheduling) ||
    (intake &&
      (!scheduling || !sameDraft(intake.schedulingDraft, scheduling))) ||
    (review && (!intake || !sameDraft(review.intakeDraft, intake))) ||
    (paymentUi && (!review || !sameDraft(paymentUi.reviewDraft, review)))
  ) {
    return { status: "failed" };
  }
  const sourceFingerprint =
    createAppointmentStepOneDraftFingerprint(sourceDraft);
  if (
    working &&
    (working.sourceDraftFingerprint !== sourceFingerprint ||
      (scheduling &&
        !sameDraft(working.workingDraft, scheduling.appointmentDraft)))
  ) {
    return { status: "failed" };
  }
  if (working) {
    const workingValidation = validateAppointmentBookingDraft(
      working.workingDraft,
    );
    if (
      !workingValidation.valid ||
      !isSupportedSpecialistAppointmentWorkingDraftTransition({
        sourceDraft,
        workingDraft: workingValidation.draft,
        timeProfessionalMutationEvidence:
          working.timeProfessionalMutationEvidence as AppointmentTimeProfessionalMutationEvidenceV1 | null,
        evaluatedAtEpochMs:
          isRecord(working.timeProfessionalMutationEvidence) &&
          typeof working.timeProfessionalMutationEvidence.generatedAtEpochMs ===
            "number"
            ? working.timeProfessionalMutationEvidence.generatedAtEpochMs
            : undefined,
      })
    ) {
      return { status: "failed" };
    }
  }
  if (scheduling) {
    const workingValidation = validateAppointmentBookingDraft(
      scheduling.appointmentDraft,
    );
    if (
      !workingValidation.valid ||
      !isSupportedSpecialistAppointmentWorkingDraftTransition({
        sourceDraft,
        workingDraft: workingValidation.draft,
        staffChoiceEvidence: staff as
          AppointmentStaffChoiceEvidenceV2 | undefined,
        timeProfessionalMutationEvidence:
          scheduling.timeProfessionalMutationEvidence as AppointmentTimeProfessionalMutationEvidenceV1 | null,
        evaluatedAtEpochMs:
          isRecord(scheduling.availabilityRequest) &&
          typeof scheduling.availabilityRequest.evaluatedAtEpochMs === "number"
            ? scheduling.availabilityRequest.evaluatedAtEpochMs
            : undefined,
      })
    ) {
      return { status: "failed" };
    }
  }

  const waitlistEntries: ExactLifecycleEntry[] = [];
  for (const draft of [...ownedWaitlistDrafts].sort((left, right) =>
    String(left.draftId).localeCompare(String(right.draftId)),
  )) {
    const entry = createLifecycleEntry({
      storeKey: `appointment-waitlist-v3:${String(draft.draftId)}`,
      draft,
      inspect: (draftId) => {
        const all = inspectAppointmentWaitlistDraftsPersisted();
        if (all.status === "unreadable") return { status: "unreadable" };
        const matches = all.drafts.filter(
          (candidate) => candidate.draftId === draftId,
        );
        return matches.length === 0
          ? { status: "absent" }
          : matches.length === 1
            ? { status: "present", draft: matches[0] }
            : { status: "unreadable" };
      },
      create: createAppointmentWaitlistDraftStrictPersisted,
      remove: removeAppointmentWaitlistDraftStrictPersisted,
    });
    if (!entry) return { status: "unreadable" };
    waitlistEntries.push(entry);
  }
  const pendingTransitionEntries: ExactLifecycleEntry[] = [];
  for (const transition of [...ownedPendingTransitions].sort((left, right) =>
    String(left.waitlistDraftId).localeCompare(String(right.waitlistDraftId)),
  )) {
    const waitlistDraftId = String(transition.waitlistDraftId);
    const wrapped = { draftId: waitlistDraftId, transition };
    const entry = createLifecycleEntry({
      storeKey: `appointment-waitlist-pending-auth-v1:${waitlistDraftId}`,
      draft: wrapped,
      inspect: (draftId) => {
        const all = inspectAppointmentWaitlistPendingTransitionsPersisted();
        if (all.status === "unreadable") return { status: "unreadable" };
        const matches = all.transitions.filter(
          (candidate) => candidate.waitlistDraftId === draftId,
        );
        return matches.length === 0
          ? { status: "absent" }
          : matches.length === 1
            ? {
                status: "present",
                draft: { draftId, transition: matches[0] },
              }
            : { status: "unreadable" };
      },
      create: (value) => {
        if (!isRecord(value) || !isRecord(value.transition)) {
          return { status: "invalid" };
        }
        const created =
          createAppointmentWaitlistPendingTransitionStrictPersisted(
            value.transition,
          );
        return created.status === "created"
          ? {
              status: "created",
              draft: {
                draftId: waitlistDraftId,
                transition: created.transition,
              },
            }
          : created;
      },
      remove: (value) => {
        if (!isRecord(value) || !isRecord(value.transition)) {
          return { status: "invalid" };
        }
        const removed =
          removeAppointmentWaitlistPendingTransitionStrictPersisted(
            value.transition,
          );
        return removed.status === "removed"
          ? {
              status: "removed",
              draft: {
                draftId: waitlistDraftId,
                transition: removed.transition,
              },
            }
          : removed;
      },
    });
    if (!entry) return { status: "unreadable" };
    pendingTransitionEntries.push(entry);
  }
  const entries = [
    ...optionalEntries,
    ...waitlistAttemptEntries,
    ...waitlistEntries,
    ...pendingTransitionEntries,
    source.entry,
  ];
  const mutableCleanupFingerprint = createMutableCleanupFingerprint(
    "appointment",
    entries,
  );
  return mutableCleanupFingerprint
    ? { status: "ready", snapshot: { entries, mutableCleanupFingerprint } }
    : { status: "unreadable" };
}

function createPackageLifecycleInspection(
  sourceDraft: PackagePurchaseDraftV1,
  expectedCustomerAccountId: string | null,
): ExactLifecycleInspection {
  const destinationDraftId = sourceDraft.draftId;
  const receipt = inspectCustomerOwnedTerminalDraft(
    inspectPackagePurchaseReceiptPersisted,
    destinationDraftId,
    expectedCustomerAccountId,
  );
  if (receipt === "unreadable") return { status: "unreadable" };
  if (receipt === "failed") return { status: "failed" };
  if (receipt === "blocked") return { status: "blocked" };
  const attempt = inspectOptionalEntry({
    storeKey: "package-purchase-attempt-v1",
    draftId: destinationDraftId,
    inspect: inspectPackagePurchaseAttemptPersisted,
    create: createPackagePurchaseAttemptStrictPersisted,
    remove: removePackagePurchaseAttemptStrictPersisted,
  });
  if (attempt.status === "unreadable") return { status: "unreadable" };
  if (
    attempt.status === "present" &&
    (!validatePackagePurchaseAttempt(attempt.entry.draft).valid ||
      attempt.entry.draft.customerAccountId !== expectedCustomerAccountId ||
      !sameDraft(attempt.entry.draft.draft, sourceDraft))
  ) {
    return { status: "failed" };
  }
  if (
    attempt.status === "present" &&
    (!isRecord(attempt.entry.draft.state) ||
      attempt.entry.draft.state.kind !== "terminal" ||
      attempt.entry.draft.state.failure !== "purchase-provider-unavailable")
  ) {
    return { status: "blocked" };
  }
  const source = inspectOptionalEntry({
    storeKey: "package-purchase-source-v1",
    draftId: destinationDraftId,
    inspect: inspectPackagePurchaseDraftPersisted,
    create: createPackagePurchaseDraftStrictPersisted,
    remove: removePackagePurchaseDraftStrictPersisted,
  });
  if (source.status !== "present") return { status: source.status };
  if (!sameDraft(source.entry.draft, sourceDraft)) return { status: "failed" };
  const entries = [
    ...(attempt.status === "present" ? [attempt.entry] : []),
    source.entry,
  ];
  const mutableCleanupFingerprint = createMutableCleanupFingerprint(
    "package",
    entries,
  );
  return mutableCleanupFingerprint
    ? { status: "ready", snapshot: { entries, mutableCleanupFingerprint } }
    : { status: "unreadable" };
}

export interface SpecialistLocationGatewayCommittedDestinationAdapters {
  destinationDraftId: string;
  expectedDestinationFingerprint: string;
  destinationInspector: SpecialistLocationGatewayPostDraftDestinationInspector;
  destinationLifecycle: SpecialistLocationGatewayDestinationLifecycle;
}

function createPostDraftDestinationInspector(input: {
  destinationDraftId: string;
  persistedFingerprint: string;
  inspect(draftId: string): unknown;
  expectedDraft: unknown;
}): SpecialistLocationGatewayPostDraftDestinationInspector {
  return {
    async inspectExact() {
      let raw: unknown;
      try {
        raw = input.inspect(input.destinationDraftId);
      } catch {
        return { status: "unreadable" };
      }
      const inspected = snapshotPersistedDraftInspection(raw);
      if (!inspected || inspected.status === "unreadable") {
        return { status: "unreadable" };
      }
      if (inspected.status === "absent") return { status: "absent" };
      return sameDraft(inspected.draft, input.expectedDraft)
        ? {
            status: "present-exact",
            destinationDraftId: input.destinationDraftId,
            persistedFingerprint: input.persistedFingerprint,
          }
        : { status: "failed" };
    },
  };
}

export function createSpecialistLocationGatewayAppointmentDestinationAdapters(
  value: unknown,
  expectedCustomerAccountId: string | null,
): SpecialistLocationGatewayCommittedDestinationAdapters | null {
  const validation = validateAppointmentStepOneBookingDraft(value);
  if (!validation.valid) return null;
  const persistedFingerprint =
    createSpecialistLocationGatewayAppointmentDestinationFingerprint(
      validation.draft,
    );
  if (!persistedFingerprint) return null;
  return {
    destinationDraftId: validation.draft.draftId,
    expectedDestinationFingerprint: persistedFingerprint,
    destinationInspector: createPostDraftDestinationInspector({
      destinationDraftId: validation.draft.draftId,
      persistedFingerprint,
      inspect: inspectAppointmentBookingDraftPersisted,
      expectedDraft: validation.draft,
    }),
    destinationLifecycle: createDestinationLifecycle({
      destinationDraftId: validation.draft.draftId,
      persistedFingerprint,
      inspectAggregate: () =>
        createAppointmentLifecycleInspection(
          validation.draft,
          expectedCustomerAccountId,
        ),
    }),
  };
}

export function createSpecialistLocationGatewayPackageDestinationAdapters(
  value: unknown,
  expectedCustomerAccountId: string | null,
): SpecialistLocationGatewayCommittedDestinationAdapters | null {
  const validation = validatePackagePurchaseDraft(value);
  if (!validation.valid) return null;
  const persistedFingerprint =
    createSpecialistLocationGatewayPackageDestinationFingerprint(
      validation.draft,
    );
  if (!persistedFingerprint) return null;
  return {
    destinationDraftId: validation.draft.draftId,
    expectedDestinationFingerprint: persistedFingerprint,
    destinationInspector: createPostDraftDestinationInspector({
      destinationDraftId: validation.draft.draftId,
      persistedFingerprint,
      inspect: inspectPackagePurchaseDraftPersisted,
      expectedDraft: validation.draft,
    }),
    destinationLifecycle: createDestinationLifecycle({
      destinationDraftId: validation.draft.draftId,
      persistedFingerprint,
      inspectAggregate: () =>
        createPackageLifecycleInspection(
          validation.draft,
          expectedCustomerAccountId,
        ),
    }),
  };
}
